package com.ijoin.platform

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.FileReader

@Composable
actual fun rememberVoiceRecorder(): VoiceRecorderState {
    var isRecording by remember { mutableStateOf(false) }
    var recordedData by remember { mutableStateOf<ByteArray?>(null) }
    var durationSeconds by remember { mutableStateOf(0) }
    var error by remember { mutableStateOf<String?>(null) }

    var mediaRecorder by remember { mutableStateOf<dynamic>(null) }
    var audioChunks by remember { mutableStateOf<MutableList<Blob>>(mutableListOf()) }
    var currentStream by remember { mutableStateOf<dynamic>(null) }

    val scope = rememberCoroutineScope()

    // Timer for duration
    LaunchedEffect(isRecording) {
        if (isRecording) {
            durationSeconds = 0
            while (isRecording) {
                delay(1000)
                durationSeconds++
                // Auto-stop at max duration
                if (durationSeconds >= VoiceRecorderState.MAX_DURATION_SECONDS) {
                    mediaRecorder?.stop()
                    isRecording = false
                }
            }
        }
    }

    return VoiceRecorderState(
        startRecording = {
            error = null
            recordedData = null
            durationSeconds = 0
            audioChunks = mutableListOf()

            try {
                val navigator = js("navigator")
                val mediaDevices = navigator.mediaDevices

                if (mediaDevices == null || mediaDevices == undefined) {
                    error = "Audio recording not supported in this browser"
                    return@VoiceRecorderState
                }

                val constraints = js("({ audio: true })")

                mediaDevices.getUserMedia(constraints).then { stream: dynamic ->
                    currentStream = stream

                    val options = js("({ mimeType: 'audio/webm' })")
                    val recorder = js("new MediaRecorder(stream, options)")
                    mediaRecorder = recorder

                    recorder.ondataavailable = { event: dynamic ->
                        val blob = event.data.unsafeCast<Blob>()
                        audioChunks.add(blob)
                    }

                    recorder.onstop = {
                        // Convert chunks to blob then to ByteArray
                        val blobArray = audioChunks.toTypedArray()
                        val finalBlob = Blob(blobArray, BlobPropertyBag(type = "audio/webm"))
                        val reader = FileReader()

                        reader.onloadend = { _ ->
                            val arrayBuffer = reader.result.unsafeCast<ArrayBuffer>()
                            val uint8Array = Uint8Array(arrayBuffer)
                            val length = uint8Array.length
                            val bytes = ByteArray(length)
                            for (i in 0 until length) {
                                bytes[i] = uint8Array[i]
                            }
                            recordedData = bytes
                            Unit
                        }

                        reader.readAsArrayBuffer(finalBlob)

                        // Stop all tracks
                        val tracks = stream.getTracks()
                        val trackCount = tracks.length.unsafeCast<Int>()
                        for (i in 0 until trackCount) {
                            tracks[i].stop()
                        }
                    }

                    recorder.start()
                    isRecording = true
                }.catch { err: dynamic ->
                    error = "Microphone access denied: ${err.message}"
                    isRecording = false
                }
            } catch (e: Exception) {
                error = "Failed to start recording: ${e.message}"
                isRecording = false
            }
        },
        stopRecording = {
            try {
                mediaRecorder?.stop()
            } catch (e: Exception) {
                error = "Failed to stop recording: ${e.message}"
            }
            isRecording = false
        },
        isRecording = isRecording,
        recordedData = recordedData,
        durationSeconds = durationSeconds,
        error = error,
        clear = {
            recordedData = null
            error = null
            durationSeconds = 0
            audioChunks = mutableListOf()
        },
        audioFormat = "webm"
    )
}
